import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath } from 'react-router';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import {
  MLB_TEAMS_SLUG,
  NBA_TEAMS_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import type { MLBTeams } from '@sorare/core/src/lib/mlbTeams';
import type { NBATeams } from '@sorare/core/src/lib/nbaTeams';
import { Baseball } from '@sorare/core/src/routing/MultiSportAppBar/Sport/Baseball';
import { NBA } from '@sorare/core/src/routing/MultiSportAppBar/Sport/NBA';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { Header } from '#src/LicensedPartners/PartnersList/Header/index.tsx';
import { Item } from '#src/LicensedPartners/PartnersList/Item/index.tsx';
import { StickySectionTitle } from '#src/LicensedPartners/PartnersList/StickySectionTitle/index.tsx';

const ClubsList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  padding-top: var(--double-unit);
  @media ${laptopAndAbove} {
    grid-template-columns: repeat(4, 1fr);
    margin-left: 270px;
    margin-top: -100px;
    padding-top: 0;
  }
  margin-bottom: var(--quadruple-unit);
  gap: var(--double-unit);
`;

type Props = {
  clubs: typeof MLBTeams | typeof NBATeams;
  sport: typeof Sport.NBA | typeof Sport.BASEBALL;
};

const CLUBS_USING_MONOCHROME_LOGO = ['utah-jazz'];

export const USSportClubs = ({ clubs, sport }: Props) => {
  const [searchValue, setSearchValue] = useState('');

  const { formatMessage } = useIntl();
  const clubsToDisplay = useMemo(() => {
    const result = searchValue.trim()
      ? clubs
          ?.filter(club =>
            club.fullName.toLowerCase().includes(searchValue.toLowerCase())
          )
          .sort((a, b) => a.fullName.localeCompare(b.fullName))
      : clubs.slice().sort((a, b) => a.fullName.localeCompare(b.fullName));
    return result;
  }, [clubs, searchValue]);
  return (
    <>
      <Header
        onSearchInputChange={setSearchValue}
        searchPlaceholder={
          sport === Sport.NBA
            ? formatMessage({
                defaultMessage: 'Search for an NBA team',
              })
            : formatMessage({
                defaultMessage: 'Search for an MLB team',
              })
        }
      />
      <>
        <StickySectionTitle href={`#${sport}`}>
          <Horizontal>
            {sport === Sport.BASEBALL ? <Baseball /> : <NBA />}
          </Horizontal>
        </StickySectionTitle>
        <ClubsList id={sport}>
          {clubsToDisplay.map(club => (
            <Item
              key={club.slug}
              name={club.fullName}
              logoUrl={
                CLUBS_USING_MONOCHROME_LOGO.includes(club.slug)
                  ? club.monochromeSvgUrl
                  : club.svgUrl || ''
              }
              to={
                sport === Sport.BASEBALL
                  ? generatePath(MLB_TEAMS_SLUG, { slug: club.slug })
                  : generatePath(NBA_TEAMS_SLUG, { slug: club.slug })
              }
            />
          ))}
        </ClubsList>
      </>
    </>
  );
};
